import React, { useMemo } from "react";
import "./ScheduleView.css";

const STAGE_HEADER_H = 40; // per-column header

const ScheduleView = ({ data, width, height, scheduleSelection, handleChangeUserSelections, userSelections, showGroupSchedules }) => {
    // data: { date: "YYYY-MM-DD", stages: [...] }
    const gridH = Math.max(0, height - STAGE_HEADER_H);
    const colCount = Math.max(1, (data?.stages || []).length);

    const layoutDay = useMemo(() => {
        if (!data) return null;

        const dayStartMs = Number.isFinite(data.dayStart) ? toMsFromDateAndHours(data.date, data.dayStart) : null;
        const dayEndMs = Number.isFinite(data.dayEnd) ? toMsFromDateAndHours(data.date, data.dayEnd) : null;

        // collect times and cache ms on sets
        const times = [];
        data.stages.forEach(stage => {
            stage.sets.forEach(s => {
                if (!Number.isFinite(s.start) || !Number.isFinite(s.end)) return;
                const startMs = toMsFromDateAndHours(data.date, s.start);
                const endMs = toMsFromDateAndHours(data.date, s.end);
                times.push({ startMs, endMs });
                s._startMs = startMs;
                s._endMs = endMs;
            });
        });

        if (dayStartMs != null && dayEndMs != null) {
            const totalMs = Math.max(1, dayEndMs - dayStartMs);
            return { minMs: dayStartMs, maxMs: dayEndMs, totalMs };
        }

        if (times.length === 0) return null;

        const rawMin = Math.min(...times.map(t => t.startMs));
        const rawMax = Math.max(...times.map(t => t.endMs));
        const minMs = floorToHalfHour(rawMin);
        const maxMs = ceilToHalfHour(rawMax);
        const totalMs = Math.max(1, maxMs - minMs);
        return { minMs, maxMs, totalMs };
    }, [data]);

    if (!data || !layoutDay) return null;

    const { minMs, maxMs, totalMs } = layoutDay;

    return (
        <div
            className="schedule-view-container"
            style={{
                width: `${width}px`,
                height: `${height}px`,
            }}>
            {/* Stage headers */}
            <div
                className="stage-header-container"
                style={{
                    borderTop: '1px solid #ffffff66',
                    gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))`,
                    height: `${STAGE_HEADER_H}px`,
                }}
            >
                {data.stages.map((s, i) => (
                    <div
                        key={i}
                        className="stage-header"
                        data-stage={`stage-${s.stage}`}
                        title={s.stage}
                        style={{
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        <img
                            src={`/headers/${s.stage.replace(' ', '')}.jpg`}
                            alt={s.stage}
                            onError={(e) => {
                                e.target.style.display = 'none';
                                const fallback = document.createElement('div');
                                fallback.innerHTML = String(s.stage).replace(/ /g, '<br />');
                                fallback.style.whiteSpace = 'pre-wrap';
                                e.target.parentNode.appendChild(fallback);
                            }}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'center',
                                display: 'block',
                            }}
                        />
                    </div>
                ))}
            </div>

            {/* Grid */}
            <div
                className='set-times-container'
                style={{
                    height: `${gridH}px`,
                    gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))`,
                }}
            >
                {data.stages.map((stage, colIdx) => (
                    <div
                        className="set-times-column-container"
                        data-stage={`stage-${stage.stage}`}
                        key={colIdx}
                    >
                        {stage.sets.filter(set => Number.isFinite(set.start) && Number.isFinite(set.end)).map((set) => {
                            const clampedStart = Math.max(minMs, set._startMs);
                            const clampedEnd = Math.min(maxMs, set._endMs);
                            const top = ((clampedStart - minMs) / totalMs) * gridH;
                            const heightPx = Math.max(2, ((clampedEnd - clampedStart) / totalMs) * gridH);
                            // const title = joinArtistsB2B(set.artists);
                            const titleNode = joinArtistsB2BNode(set.artists);
                            const titleText = joinArtistsB2BText(set.artists);
                            const setRanking = userSelections.selections.find(item => item.id === set.id)?.rank || 0;

                            const avgRanking = (
                                set.artists.reduce((sum, artist) => sum + parseFloat(artist.ranking), 0) / set.artists.length
                            );
                            const percent = Math.round(avgRanking * 100) / 5;
                            const setAvgRankingBorderColor = percent > 0 ? getColor(percent) : null;
                            const friends4 = set.friendSchedules?.filter(item => item.schedule === 4) || null;
                            const friends3 = set.friendSchedules?.filter(item => item.schedule === 3) || null;
                            return (
                                <div
                                    key={set.id}
                                    onClick={() => handleChangeUserSelections(set.id, scheduleSelection, titleText)}
                                    className={"set-times "
                                        + (setRanking > 0 ? `ranking-${setRanking} ` : ' ')
                                        + (percent > 0 ? ' user-ranking' : '')
                                    }
                                    style={{
                                        top: `${top}px`,
                                        height: `${heightPx}px`,
                                        borderColor: setAvgRankingBorderColor
                                    }}
                                    title={`${titleText} — ${fmtTimeFromHours(set.start)}`}
                                >
                                    {(friends4 || friends3) && showGroupSchedules && <div className="set-times-friends-rankings">
                                        {friends4.length > 0 && <div className="set-times-friends-rankings-4">
                                            {friends4.length}
                                        </div>}
                                        {friends3.length > 0 && <div className="set-times-friends-rankings-3">
                                            {friends3.length}
                                        </div>}
                                    </div>}
                                    <div className="set-times-title"                                    >
                                        {titleNode}
                                    </div>
                                    <div className="set-times-time">
                                        {fmtTimeFromHours(set.start)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default ScheduleView;



// ---------- utils ----------
const toMsFromDateAndHours = (dateISO, hoursDecimal) => {
    const base = new Date(`${dateISO}T00:00:00Z`).getTime();
    return base + hoursDecimal * 3600 * 1000;
}
const floorToHalfHour = (ms) => {
    const half = 30 * 60 * 1000;
    return Math.floor(ms / half) * half;
}
const ceilToHalfHour = (ms) => {
    const half = 30 * 60 * 1000;
    return Math.ceil(ms / half) * half;
}
const fmtTimeFromHours = (hoursDecimal) => {
    const normalized = ((hoursDecimal % 24) + 24) % 24;
    const hour24 = Math.floor(normalized);
    const minute = Math.round((normalized - hour24) * 60);
    const hour12 = hour24 % 12 || 12;
    return `${hour12}:${String(minute).padStart(2, '0')}`;
}
const joinArtistsB2BNode = (artists = []) =>
    artists.reduce((acc, a, i) => {
        if (i > 0) acc.push(<span key={`sep-${i}`} className="b2b"> b2b </span>);
        acc.push(<span key={`name-${i}`}>{a.name}</span>);
        return acc;
    }, []);

const joinArtistsB2BText = (artists = []) =>
    artists.map(a => a.name).join(" b2b ");


// Function to linearly interpolate between two values
const interpolate = (value, start, end) => {
    return Math.round(start + (end - start) * value);
}

// Function to get the color between two color stops
const interpolateColor = (value, color1, color2) => {
    const r = interpolate(value, color1[0], color2[0]);
    const g = interpolate(value, color1[1], color2[1]);
    const b = interpolate(value, color1[2], color2[2]);

    // Return the interpolated color as a hex string
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
}

// Main function to return a hex color based on input between 1 and 5
const getColor = (value) => {
    value /= 20;

    if (parseInt(value) === -1) return '#808080';

    // Define color stops in RGB
    const clamped = Math.max(0, Math.min(value, 5));

    const colorStops = [
        [128, 0, 0],   // 0 - Dark Red
        [255, 0, 0],   // 1 - Red
        [255, 128, 0], // 2 - Orange
        [255, 255, 0], // 3 - Yellow
        [0, 168, 107],   // 4 - Green
        [0, 191, 255]    // 5 - Blue
    ];

    const baseIndex = Math.floor(clamped);
    const nextIndex = Math.min(baseIndex + 1, colorStops.length - 1);
    const fraction = clamped - baseIndex;

    return interpolateColor(fraction, colorStops[baseIndex], colorStops[nextIndex]);
}
